import { useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const WIDTH = 320;
const HEIGHT = 180;
const GAP = 8;

/**
 * A site's name that shows the picture of its front page while the pointer is
 * on it (ADR-0034). The picture is drawn over the page rather than inside the
 * table, which would cut it at its edge, and is loaded only when shown.
 */
export function SitePreviewHover({
  siteId,
  takenAt,
  children,
}: {
  readonly siteId: string;
  /** Null before the first picture: then there is nothing to show. */
  readonly takenAt: string | null;
  readonly children: ReactNode;
}): ReactNode {
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);
  if (!takenAt) return children;

  const show = (element: HTMLElement): void => {
    const box = element.getBoundingClientRect();
    // Below the name when it fits, above it when the name is near the bottom.
    const below = box.bottom + GAP + HEIGHT <= window.innerHeight;
    setAt({ left: Math.min(box.left, window.innerWidth - WIDTH - GAP), top: below ? box.bottom + GAP : box.top - GAP - HEIGHT });
  };

  return (
    <span onMouseEnter={(event) => show(event.currentTarget)} onMouseLeave={() => setAt(null)}>
      {children}
      {at
        ? createPortal(
            <img
              className="site-preview-hover"
              src={`/api/v1/sites/${siteId}/preview?t=${encodeURIComponent(takenAt)}`}
              alt=""
              width={WIDTH}
              height={HEIGHT}
              style={{ left: at.left, top: at.top }}
            />,
            document.body,
          )
        : null}
    </span>
  );
}
